import React from 'react'
import {
  ActivityProgress,
  CustomType,
  GoalWithProgress,
  ProgressType,
  UserCustomDailyProgress,
  UserDailyDiaryProgress,
  UserDailyProgress,
} from '../../services/api.types'
import { t } from 'ttag'
import { formatThousands } from '../../common/helpers/translations'
import { ReactComponent as GoalIcon } from '../../assets/v3/icons/ranking/goal.svg'
import { ReactComponent as GoalAchievedIcon } from '../../assets/v3/icons/ranking/goal-achieved.svg'
import { getCustomTypeText, getCustomUnitText } from '../../services/CustomTypes'
import { getChallengeDurationInDays } from '../../common/helpers/date'
import {
  getChallengeGoalUnits,
  getChallengeProgressCurrently,
  getChallengeProgressGoal,
  getChallengeProgressUnits,
  getPersistenceDailyTotal,
  isActivityIndexType,
  isAutomatedType,
  isCollectiveType,
  isCustomType,
  isDistanceType,
  isPersistenceType,
} from '../../common/helpers/challenges'
import makeStyles from '@material-ui/core/styles/makeStyles'
import YumGrid from '../YumGrid/YumGrid'
import { darkBlue, lightGreen } from '../../styles/material-ui-theme-v2'
import YumConsistencyPills, { ConsistencyProgress } from './components/YumConsistencyPills/YumConsistencyPills'
import { isRtl } from '../../i18nInit'

export interface IYumChallengeProgressProps {
  goal: GoalWithProgress
  activityProgress: ActivityProgress
  dailyDiary: UserDailyDiaryProgress[]
  customProgress?: UserCustomDailyProgress[]
  progressResults: UserDailyProgress[]
  // These can be removed later
  showDetails: boolean
  toggleShowDetails: () => void
  isNewLayout?: boolean
}

const useStyles = makeStyles((theme) => ({
  main: {
    padding: '10px',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    color: 'white',
  },
  header: {
    display: 'flex',
    //gap: 10,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    marginBottom: '32px',
    userSelect: 'none',
  },
  headerIcon: {
    flexShrink: 0,
    marginRight: isRtl ? 0 : '10px',
    marginLeft: isRtl ? '10px' : 0,
  },
  totalGoalLabel: {
    fontWeight: 500,
    fontSize: 12,
    lineHeight: '14px',
  },
  totalGoalText: {
    fontWeight: 500,
    fontSize: 15,
    lineHeight: '18px',
  },
  progressCurrently: {
    fontWeight: 500,
    fontSize: 28,
    lineHeight: '33px',
  },
  progressGoal: {
    fontWeight: 500,
    fontSize: 28,
    lineHeight: '33px',
    opacity: 0.8,
  },
  progressUnits: {
    fontWeight: 500,
    fontSize: 18,
    lineHeight: '21px',
    opacity: 0.8,
  },
  progressBar: {
    borderRadius: 10,
    height: 8,
    marginTop: 8,
    background: 'rgba(255, 255, 255, 0.4)',
  },
  progressBarInner: {
    borderRadius: 10,
    height: 8,
    marginTop: 8,
    background: 'white',
    transition: 'all 0.2s ease-out',
  },

  footer: {
    display: 'flex',
    //gap: 10,
    flexDirection: 'column',
    position: 'relative',
    padding: '10px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  footerInner: {
    display: 'flex',
    justifyContent: 'space-between',
    //gap: 10,
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '16px',
    marginBottom: '10px',
  },
  challengeLength: {
    paddingTop: '1px',
  },
  footerButton: {
    display: 'inline-block',
    padding: '0 8px',
    lineHeight: '16px',
    fontSize: 12,
    fontWeight: 'bold',
    borderRadius: 6,
    width: 'auto',
    minWidth: 'auto',
    margin: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    height: '20px',
  },
}))

export default function YumChallengeProgress(props: IYumChallengeProgressProps) {
  const classes = useStyles()
  const {
    goal,
    activityProgress,
    dailyDiary,
    customProgress,
    progressResults,
    showDetails,
    toggleShowDetails,
    isNewLayout,
  } = props

  const {
    id: goalId,
    customName,
    customType,
    customUnit,
    customGoal,
    persistenceGoal,
    dailyPersistenceGoal,
    progressType,
    participating,
    startDate,
    endDate,
    activityFormulaTarget,
    collectivePercentage,
    manualEntryEnabled,
  } = goal
  const isPersistenceCustom = progressType === ProgressType.PERSISTENCE_CUSTOM
  const isPersistenceCustomOrBinary =
    progressType === ProgressType.PERSISTENCE_CUSTOM || progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY
  const isCustom = isCustomType(progressType)
  const isCustomCustom = customType === CustomType.CUSTOM
  const isPersistence = isPersistenceType(progressType)
  const isActivityIndex = isActivityIndexType(progressType)
  const isPersistenceOrCustom = isPersistence || isCustom

  const customUnits = getCustomUnitText(customType, customType, customUnit) as string
  const daysNumber = getChallengeDurationInDays(startDate, endDate)

  const isDistance = isDistanceType(customType)
  const isAutomated = isAutomatedType(customType)
  const isBinary = progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY

  const getPillsProgress = (manualEntryEnabled: boolean): ConsistencyProgress[] => {
    const dailyGoal = isBinary || dailyPersistenceGoal === null ? 1 : dailyPersistenceGoal || 0
    if (isActivityIndex) {
      return progressResults.length !== daysNumber
        ? []
        : progressResults.map((item, keyIndex) => {
            const total = getPersistenceDailyTotal(item, progressType, manualEntryEnabled)
            const { date } = item
            const achieved = total >= dailyGoal && participating
            return { date, achieved }
          })
    } else if (isCustomCustom || isPoints) {
      return customProgress?.length !== daysNumber
        ? []
        : customProgress.map((item, keyIndex) => {
            const { date, value } = item
            const achieved = value >= dailyGoal && participating
            return { date, achieved }
          })
    } else {
      return dailyDiary?.length !== daysNumber
        ? []
        : dailyDiary.map((item, keyIndex) => {
            const { date, sum, integrationValue } = item
            const achieved = (manualEntryEnabled ? sum >= dailyGoal : integrationValue >= dailyGoal) && participating
            return { date, achieved }
          })
    }
  }

  // const isActivityIndex = progressType === ProgressType.ALL
  const isCollective = isCollectiveType(progressType)
  const isPoints = customType === CustomType.POINTS
  // console.log('isPoints', isPoints, customType)

  // IMPORTANT NUMBERS SHOWN
  const showDigits = isDistance && !isCollective && !isPersistenceCustomOrBinary ? 2 : undefined
  const progressCurrently = formatThousands(getChallengeProgressCurrently(goal, activityProgress), showDigits)
  const progressGoal = formatThousands(getChallengeProgressGoal(goal, activityProgress))
  const progressUnits = getChallengeProgressUnits(goal)

  const getTotalGoalText = () => {
    const { stepsGoal } = goal
    if (isPersistence) {
      return t`Achieve daily target for ${persistenceGoal} days`
    } else {
      // TODO :
      const units = getChallengeGoalUnits(goal) // isCustom ? customUnits : progressUnits
      //const goalText = formatThousands((isCustom ? customGoal : stepsGoal) || 0) //activityFormulaTarget
      // translator: {100} {km} in {10} days
      //return t`${progressGoal} ${units} in ${daysNumber} days`
      return `${progressGoal} ${units}`
    }
  }

  let percentageDone: any = isCollective
    ? collectivePercentage || 0
    : activityProgress && activityProgress.totalPercentage
    ? activityProgress.totalPercentage || 0
    : 0

  const progressPercentage = percentageDone > 100 ? 100 : percentageDone
  const goalAchieved = progressPercentage === 100

  percentageDone = formatThousands(percentageDone, 0)

  const activityName = getCustomTypeText(customType, customName)

  return (
    <div>
      {isNewLayout && progressType !== ProgressType.ALL ? (
        <>
          <div
            className={classes.main}
            style={{
              color: goalAchieved ? darkBlue : 'white',
              backgroundColor: goalAchieved ? lightGreen : darkBlue,
              borderBottomLeftRadius: !isPersistence ? '8px' : '0px',
              borderBottomRightRadius: !isPersistence ? '8px' : '0px',
              marginBottom: !isPersistence ? '16px' : '0px',
            }}
          >
            <div className={classes.header}>
              <div className={classes.headerIcon}>{goalAchieved ? <GoalAchievedIcon /> : <GoalIcon />}</div>
              <YumGrid>
                <div>
                  <>
                    <div className={classes.totalGoalLabel}>
                      {isCollective ? t`Collective goal` : t`Total goal`}
                      {activityName && !isCustomCustom && !isPersistence && isAutomated
                        ? ` (${activityName.toLowerCase()})`
                        : ''}
                    </div>
                    <div className={classes.totalGoalText}>{getTotalGoalText()}</div>
                  </>
                  {/*)}*/}
                </div>
                {/*{showManualEntryButton && <YumLogActivityWrapper goal={goal} />}*/}
              </YumGrid>
            </div>
            <YumGrid>
              <div>
                <span className={classes.progressCurrently}>{progressCurrently}</span>
                {!isPersistence ? (
                  <>
                    <span className={classes.progressUnits}> {progressUnits}</span>
                  </>
                ) : (
                  <>
                    <span className={classes.progressGoal}> / {progressGoal}</span>{' '}
                    <span className={classes.progressUnits}>{progressUnits}</span>
                  </>
                )}
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                <span className={classes.progressCurrently}>{percentageDone}</span>
                <span className={classes.progressUnits}> %</span>
              </div>
            </YumGrid>
            <div className={classes.progressBar}>
              <div
                className={classes.progressBarInner}
                style={{ width: `${progressPercentage}%`, backgroundColor: goalAchieved ? darkBlue : 'white' }}
              />
            </div>
          </div>

          {isPersistence && (
            <div
              className={classes.footer}
              style={{
                backgroundColor: isNewLayout ? 'white' : '#f8f8f8',
                marginBottom: isNewLayout ? '16px' : '32px',
              }}
            >
              <div className={classes.footerInner}>
                <span className={classes.challengeLength}>{t`Challenge length: ${daysNumber} days`}</span>
                {/*{isCustom && (
                  <YumButton className={classes.footerButton} onClick={toggleShowDetails}>
                    {!showDetails ? t`Show details` : t`Hide details`}
                  </YumButton>
                )}*/}
              </div>
              {isPersistence && <YumConsistencyPills progress={getPillsProgress(manualEntryEnabled)} />}
            </div>
          )}
        </>
      ) : (
        ''
      )}
    </div>
  )
}
